<script setup lang="ts">
import { ref, onMounted, toRefs, onUpdated } from 'vue';
import UserRemoveReferral from './UserRemoveReferral.vue';
import Calendar from 'primevue/calendar';
import InputMask from 'primevue/inputmask';
import { EMIT, PERMISSIONS, REFERRAL_TYPES } from '@/enum';
import { useStudentReferralStore, useClinicianOrganizationStore } from '@/store';
import { can, useDateTime } from '@/composables';
import { ReferralProcessInterface } from '@/types';
import { OrganizationBuildingInterface } from '@/types/BuildingInterface';

interface Props {
  save: boolean;
  organizationId?: number;
  buildings: OrganizationBuildingInterface[];
}

const emit = defineEmits([EMIT.SAVE]);
const props = defineProps<Props>();
const { save, organizationId } = toRefs(props);
const removeIndex = ref<number>(0);
const openRemove = ref<boolean>(false);

const referralStore = useStudentReferralStore();
const clinicianStore = useClinicianOrganizationStore();

const validRow = (row) => {
  let valid = false;
  row.errors = null;
  const errorMessage = 'All required fields must be filled out.';

  if (row.row_status.completed) {
    return true;
  }

  if (row.id.value === '') {
    row.errors = null;
    return false;
  }

  if (
    row.id.value !== '' &&
    row.first_name.value !== '' &&
    row.last_name.value !== '' &&
    row.reason.value &&
    organizationId.value &&
    row.row_status.disable === false &&
    row.row_status.processing === false
  )
    valid = true;
  else if (
    !row.row_status.disable &&
    !row.row_status.processing &&
    (row.first_name.value !== '' || row.last_name.value !== '' || row.reason.value || row.id.value !== '')
  ) {
    row.errors = errorMessage;
  }

  return valid;
};

onUpdated(async () => {
  if (save.value) {
    referralStore.mTSSReferralTableData.forEach(async (row, index) => {
      if (!row.row_status.completed) {
        setTimeout(async () => {
          if (validRow(row)) {
            row.row_status.disable = true;
            row.row_status.processing = true;

            const payload: ReferralProcessInterface = {
              organization_id: organizationId.value as number,
              referral_type_id: row.referral_type.value,
              referral_reason_id: row.reason.value,
              referral_urgency_id: row.urgency.value,
              student_id: row.id.value,
              student_sis_id: row.student_sis_id.value,
              student_first_name: row.first_name.value,
              student_last_name: row.last_name.value,
              student_country: row.student_country.value,
              student_state: row.student_state.value,
              student_city: row.student_city.value,
              zipcode: row.zip.value,
              building_campus: props.buildings.find((b) => b.id === row.location.value)?.name,
              building_id: row.location.value,
              service_provider_id: row.service_provider.value,
              gender: row.gender.value,
              birthdate: row.birthdate.value ? useDateTime().formatDateForApi(new Date(row.birthdate.value)) : '',
              grade: row.grade.value,
              phone: row.phone.value,
              parent_sis_id: row.parent_sis_id.value,
              parent_first_name: row.parent_first_name.value,
              parent_last_name: row.parent_last_name.value,
              parent_email: row.parent_email.value,
              integration_provider_type_slug: row.integration_provider_type_slug.value,
            };

            await referralStore.process(payload, index, REFERRAL_TYPES.MTSS);

            handleProcessedRow(row);

            if (referralStore.error) referralStore.error = false;
          }
        }, index * 500);
      }
    });

    scrollBack();
    emit(EMIT.SAVE);
  }
});

const scrollBack = () => {
  const scrollableContainer = document.getElementById('mtssTable');
  const childTable = scrollableContainer?.getElementsByClassName('p-datatable-wrapper');
  if (childTable)
    childTable[0]?.scroll({
      left: 0,
      behavior: 'smooth',
    });
};

const handleProcessedRow = (row) => {
  const errorLength = row.errors?.length;
  row.row_status.disable = !errorLength;
  row.row_status.completed = !errorLength;
  row.row_status.processing = false;

  if (!errorLength) row.errors = null;
};

const init = () => {
  referralStore.mTSSReferralTableData = [];
  for (let i = 0; i < 3; i++) makeBaseRow(i);
};

const onUpdateRemove = () => {
  openRemove.value = false;
  referralStore.mTSSReferralTableData.splice(removeIndex.value, 1);
};

const onRemoveRow = (index: number) => {
  if (referralStore.mTSSReferralTableData.length > 1) {
    removeIndex.value = index;
    openRemove.value = true;
  }
};

const makeBaseRow = (i: number) => {
  const referralType = REFERRAL_TYPES.MTSS;
  const referralTypeId = referralStore.getReferralTypeId(referralType);
  const referral = referralStore.newRow(referralType);

  if (referral) {
    referral.referral_type.value = referralTypeId as number;
    referral.key = i;
    referralStore.mTSSReferralTableData.push(referral);
  }
};

onMounted(() => init());
</script>

<template>
  <UserRemoveReferral :open="openRemove" @update="onUpdateRemove" @cancel="openRemove = false" />
  <DataTable
    id="mtssTable"
    :value="referralStore.mTSSReferralTableData"
    filter-display="menu"
    class="border-solid border-1 border-100 border-round-md"
    :rows="referralStore.mTSSReferralTableData.length"
    data-key="key"
    scrollable
    scroll-direction="horizontal"
    loading-icon="logo-spinner"
    responsive-layout="scroll"
    row-group-mode="subheader"
    group-rows-by="key"
  >
    <Column style="max-width: 40px" :frozen="true">
      <template #header>
        <Icon name="add_circle" class="add__row__icon cursor-pointer" @click="makeBaseRow" />
      </template>
      <template #body="{ data, index }">
        <Icon
          v-if="!data.row_status.processing && !data.row_status.completed"
          name="remove_circle"
          class="remove__row__icon cursor-pointer"
          :value="data.id.value"
          :disabled="referralStore.mTSSReferralTableData.length < 2"
          @click="onRemoveRow(index)"
        />
        <Icon v-if="data.row_status.processing" name="cached" class="text-primary spin-cached" />
        <Icon v-if="data.row_status.completed" name="check_circle" class="text-primary" />
      </template>
    </Column>
    <Column field="referral_type" header="Referral Type" header-class="required w-screen" :frozen="true">
      <template #body="{ data }">
        <Dropdown
          v-model="data.referral_type.value"
          type="secondary"
          :options="referralStore.referralTypes"
          :disabled="data.row_status.disable"
          option-label="title"
          option-value="id"
          placeholder="Referral Type"
          class="w-full"
          @change="referralStore.onReferralTypeChange(data, REFERRAL_TYPES.MTSS)"
        />
      </template>
    </Column>
    <Column field="id" header="SSID" header-class="required" style="max-width: 150px" :frozen="true">
      <template #body="{ data }">
        <InputText v-model="data.id.value" placeholder="SSID" class="w-full" :disabled="data.row_status.disable" />
      </template>
    </Column>
    <Column field="first_name" header="Student First Name" header-class="required" style="width: 100px" :frozen="true">
      <template #body="{ data }">
        <InputText
          v-model="data.first_name.value"
          placeholder="Student First Name"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="last_name" header="Student Last Name" header-class="required" style="width: 100px" :frozen="true">
      <template #body="{ data }">
        <InputText
          v-model="data.last_name.value"
          placeholder="Student Last Name"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="zip" header="Zip Code" style="max-width: 150px">
      <template #body="{ data }">
        <InputText
          v-model="data.zip.value"
          :use-grouping="false"
          placeholder="Zip Code"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column v-if="buildings && buildings.length > 0" field="location" header="Building/Campus" style="width: 250px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.location.value"
          :options="buildings"
          option-label="name"
          option-value="id"
          placeholder="Select Building/Campus"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="reason" header="Reason of Referral" header-class="required w-screen" style="max-width: 250px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.reason.value"
          type="secondary"
          :options="referralStore.referralReasons"
          option-label="title"
          option-value="id"
          placeholder="Select Referral Reason"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="urgency" header="Urgency" style="max-width: 250px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.urgency.value"
          type="secondary"
          :options="referralStore.referralUrgencies"
          option-label="title"
          option-value="id"
          placeholder="Select Urgency"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="gender" header="Gender" style="max-width: 180px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.gender.value"
          type="secondary"
          :options="referralStore.genderOptions"
          option-label="name"
          option-value="value"
          placeholder="Select Gender"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="birthdate" header="Birth Date" style="max-width: 150px">
      <template #body="{ data }">
        <Calendar
          id="birthdate"
          v-model="data.birthdate.value"
          placeholder="mm/dd/yyyy"
          date-format="mm/dd/yy"
          :manual-input="true"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="grade" header="Grade" style="max-width: 180px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.grade.value"
          type="secondary"
          :options="referralStore.gradeOptions"
          placeholder="Select Grade"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="phone" header="Phone" style="max-width: 160px">
      <template #body="{ data }">
        <InputMask
          id="phone"
          v-model="data.phone.value"
          mask="(999) 999-9999"
          placeholder="(999) 999-9999"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="parent_first_name" header="Parent First Name" style="width: 100px">
      <template #body="{ data }">
        <InputText
          v-model="data.parent_first_name.value"
          placeholder="Parent First Name"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="parent_last_name" header="Parent Last Name" style="width: 100px">
      <template #body="{ data }">
        <InputText
          v-model="data.parent_last_name.value"
          placeholder="Parent Last Name"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="parent_email" header="Parent Email" style="width: 100px">
      <template #body="{ data }">
        <InputText
          v-model="data.parent_email.value"
          placeholder="Parent Email"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column
      v-if="can([PERMISSIONS.REFERRAL_ASSIGN])"
      field="service_provider"
      header="Service Provider"
      style="width: 250px"
    >
      <template #body="{ data }">
        <span
          v-if="!clinicianStore.clinicians || !clinicianStore.clinicians.length"
          v-text="'Please select an organization'"
        />
        <Dropdown
          v-else
          v-model="data.service_provider.value"
          type="secondary"
          :options="clinicianStore.clinicians"
          option-label="display_name"
          option-value="id"
          placeholder="Select Service Provider"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <template #groupfooter="slotProps">
      <div v-if="slotProps.data.errors" class="w-full p-3 border-bottom-1 border-100">
        <Icon name="subdirectory_arrow_right" class="mr-2 text-base text-red-600" />
        <span class="font-bold text-red-600">Errors - {{ slotProps.data.errors }}</span>
      </div>
    </template>
  </DataTable>
</template>

<style lang="scss" scoped>
.add__row__icon {
  color: var(--primary-color);
}

.remove__row__icon {
  color: $red;
}

:deep(th.required) {
  span.p-column-title::after {
    content: '*';
    color: #ff3960;
    margin-left: 0.125rem;
  }
}

.text-red-600 {
  color: $red !important;
}

:deep(.p-frozen-column) {
  margin-right: 0 !important;
  padding-right: 0 !important;
  border-right: none !important;
}

@media (min-width: 768px) {
  :deep(.p-frozen-column) {
    margin-right: 0 !important;
    padding-right: 0 !important;
    border-right: none !important;
  }
}

@media (max-width: 767px) {
  :deep(.p-frozen-column) {
    margin-right: 0 !important;
    padding-right: 0 !important;
    border-right: none !important;
  }
}
</style>
